import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { PageLoader } from './components/common/LoadingSpinner';

// Loading fallback komponenta s skeleton loaderem
const LoadingFallback = () => (
  <div className="min-h-screen bg-gray-50">
    <div className="container-custom py-8">
      {/* Skeleton pro navbar */}
      <div className="h-20 bg-white border-b border-gray-200 mb-8 animate-pulse" />
      
      {/* Skeleton pro obsah */}
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Hero skeleton */}
        <div className="h-64 bg-gray-200 section-rounded animate-pulse" />
        
        {/* Content skeleton */}
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 section-rounded w-3/4 animate-pulse" />
          <div className="h-4 bg-gray-200 section-rounded w-full animate-pulse" />
          <div className="h-4 bg-gray-200 section-rounded w-5/6 animate-pulse" />
          <div className="h-4 bg-gray-200 section-rounded w-4/5 animate-pulse" />
        </div>
        
        {/* Cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-48 bg-gray-200 section-rounded animate-pulse" />
          ))}
        </div>
      </div>
      
      {/* Skeleton pro footer */}
      <div className="h-32 bg-gray-200 mt-12 animate-pulse" />
    </div>
  </div>
);

// Code splitting - hlavní stránky (lazy loading)
const Homepage = lazy(() => import('./pages/Homepage').then(module => ({ default: module.Homepage })));
const Nis2Page = lazy(() => import('./pages/Nis2Page').then(module => ({ default: module.Nis2Page })));
const Nis2KohoSeTyka = lazy(() => import('./pages/Nis2KohoSeTyka').then(module => ({ default: module.Nis2KohoSeTyka })));
const PovinnostiNis2 = lazy(() => import('./pages/PovinnostiNis2').then(module => ({ default: module.PovinnostiNis2 })));
const PripravaNaNis2 = lazy(() => import('./pages/PripravaNaNis2').then(module => ({ default: module.PripravaNaNis2 })));
const Blog = lazy(() => import('./pages/Blog').then(module => ({ default: module.Blog })));
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Kontakt = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));

// Ostatní stránky (méně často navštěvované - také lazy)
const VyhodyNis2 = lazy(() => import('./pages/VyhodyNis2').then(module => ({ default: module.VyhodyNis2 })));
const KybernetickeIncidenty = lazy(() => import('./pages/KybernetickeIncidenty').then(module => ({ default: module.KybernetickeIncidenty })));
const Nis2Smernice = lazy(() => import('./pages/Nis2Smernice').then(module => ({ default: module.Nis2Smernice })));
const ChybyPripravaNis2 = lazy(() => import('./pages/ChybyPripravaNis2').then(module => ({ default: module.ChybyPripravaNis2 })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(module => ({ default: module.PricingPage })));
const WebinarPage = lazy(() => import('./pages/WebinarPage').then(module => ({ default: module.WebinarPage })));
const Ebook1Page = lazy(() => import('./pages/Ebook1Page').then(module => ({ default: module.Ebook1Page })));
const Ebook2Page = lazy(() => import('./pages/Ebook2Page').then(module => ({ default: module.Ebook2Page })));
const GdprPage = lazy(() => import('./pages/GdprPage').then(module => ({ default: module.GdprPage })));
const FaqPage = lazy(() => import('./pages/FaqPage').then(module => ({ default: module.FaqPage })));
const NotFound = lazy(() => import('./pages/NotFound').then(module => ({ default: module.NotFound })));
const Dekujeme = lazy(() => import('./pages/Dekujeme').then(module => ({ default: module.Dekujeme })));

// Blog články (lazy loading)
const Blog10Otazek = lazy(() => import('./pages/blog/Blog10Otazek').then(module => ({ default: module.Blog10Otazek })));
const RozdilSmerniceZakon = lazy(() => import('./pages/blog/RozdilSmerniceZakon').then(module => ({ default: module.RozdilSmerniceZakon })));
const PovinnostiNis2Blog = lazy(() => import('./pages/blog/PovinnostiNis2Blog').then(module => ({ default: module.PovinnostiNis2Blog })));
const KybernetickeIncidentyBlog = lazy(() => import('./pages/blog/KybernetickeIncidentyBlog').then(module => ({ default: module.KybernetickeIncidentyBlog })));
const Nis2SmerniceBlog = lazy(() => import('./pages/blog/Nis2SmerniceBlog').then(module => ({ default: module.Nis2SmerniceBlog })));
const Nis2NeniJenItTema = lazy(() => import('./pages/blog/Nis2NeniJenItTema').then(module => ({ default: module.Nis2NeniJenItTema })));
const InspiraceZahranici = lazy(() => import('./pages/blog/InspiraceZahranici').then(module => ({ default: module.InspiraceZahranici })));
const ChybyPripravaNis2Blog = lazy(() => import('./pages/blog/ChybyPripravaNis2Blog').then(module => ({ default: module.ChybyPripravaNis2Blog })));
const VyhodyNis2Blog = lazy(() => import('./pages/blog/VyhodyNis2Blog').then(module => ({ default: module.VyhodyNis2Blog })));
const PripravaNaNis2Blog = lazy(() => import('./pages/blog/PripravaNaNis2Blog').then(module => ({ default: module.PripravaNaNis2Blog })));
const Nis2VPraxiBlog = lazy(() => import('./pages/blog/Nis2VPraxiBlog').then(module => ({ default: module.Nis2VPraxiBlog })));
const Nis2PoCeskuBlog = lazy(() => import('./pages/blog/Nis2PoCeskuBlog').then(module => ({ default: module.Nis2PoCeskuBlog })));
const Nis2OdSplneniPovinnostiKDukazumBlog = lazy(() => import('./pages/blog/Nis2OdSplneniPovinnostiKDukazumBlog').then(module => ({ default: module.Nis2OdSplneniPovinnostiKDukazumBlog })));
const Nis2TriPilireBlog = lazy(() => import('./pages/blog/Nis2TriPilireBlog').then(module => ({ default: module.Nis2TriPilireBlog })));
const Nis2RealityCheckBlog = lazy(() => import('./pages/blog/Nis2RealityCheckBlog').then(module => ({ default: module.Nis2RealityCheckBlog })));
const DodavatelskeRetezceBlog = lazy(() => import('./pages/blog/DodavatelskeRetezceBlog').then(module => ({ default: module.DodavatelskeRetezceBlog })));
const SamoidentifikaceNis2Blog = lazy(() => import('./pages/blog/SamoidentifikaceNis2Blog').then(module => ({ default: module.SamoidentifikaceNis2Blog })));
const IncidentyNis2Blog = lazy(() => import('./pages/blog/IncidentyNis2Blog').then(module => ({ default: module.IncidentyNis2Blog })));
const AplikacePage = lazy(() => import('./pages/AplikacePage').then(module => ({ default: module.AplikacePage })));
const HlavniPovinnostiNis2Blog = lazy(() => import('./pages/blog/HlavniPovinnostiNis2Blog').then(module => ({ default: module.HlavniPovinnostiNis2Blog })));

function App() {
  return (
    <BrowserRouter>
      <Layout>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
          {/* Homepage */}
          <Route path="/" element={<Homepage />} />
          
          {/* NIS2 Informační stránky */}
          <Route path="/nis2" element={<Nis2Page />} />
          <Route path="/vyhody-nis2" element={<VyhodyNis2 />} />
          <Route path="/priprava-na-nis2" element={<PripravaNaNis2 />} />
          <Route path="/nis2-koho-se-tyka" element={<Nis2KohoSeTyka />} />
          <Route path="/kyberneticke-incidenty" element={<KybernetickeIncidenty />} />
          <Route path="/povinnosti-nis2" element={<PovinnostiNis2 />} />
          <Route path="/nis2-smernice" element={<Nis2Smernice />} />
          <Route path="/chyby-priprava-nis2" element={<ChybyPripravaNis2 />} />
          
          {/* Blog */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/10-otazek-ktere-si-musi-polozit-kazda-firma-pred-nis2" element={<Blog10Otazek />} />
          <Route path="/rozdil-mezi-smernici-a-zakonem-proc-nis2-jeste-neni-povinnosti" element={<RozdilSmerniceZakon />} />
          <Route path="/blog/povinnosti-nis2-kompletni-pruvodce" element={<PovinnostiNis2Blog />} />
          <Route path="/hlavni-povinnosti-podle-nis2-a-noveho-zakona-co-presne-musite-delat" element={<HlavniPovinnostiNis2Blog />} />
          <Route path="/blog/jak-spravne-hlasit-kyberneticke-incidenty" element={<KybernetickeIncidentyBlog />} />
          <Route path="/nis2-smernice-kyberneticka-bezpecnost-eu" element={<Nis2SmerniceBlog />} />
          <Route path="/nis2-neni-jen-it-tema-proc-by-mel-jednat-management" element={<Nis2NeniJenItTema />} />
          <Route path="/jak-se-pripravuji-firmy-v-zahranici-inspirace-z-nizozemska-a-nemecka" element={<InspiraceZahranici />} />
          <Route path="/blog/5-kritickych-chyb-pri-priprave-na-nis2" element={<ChybyPripravaNis2Blog />} />
          <Route path="/blog/vyhody-nis2-jak-nova-smernice-posili-bezpecnost" element={<VyhodyNis2Blog />} />
          <Route path="/blog/priprava-na-nis2-co-vas-ceka-a-nemine" element={<PripravaNaNis2Blog />} />
          <Route path="/nis2-v-praxi-co-se-prave-deje-a-co-musite-stihnout-do-konce-roku-2025" element={<Nis2VPraxiBlog />} />
          <Route path="/nis2-po-cesku-12-mesicu-ktere-rozhodnou" element={<Nis2PoCeskuBlog />} />
          <Route path="/nis2-od-splneni-povinnosti-k-dukazum-ktere-ustoji-audit" element={<Nis2OdSplneniPovinnostiKDukazumBlog />} />
          <Route path="/nis2-v-praxi-tri-pilire-uspesneho-projektu-pravo-lide-technologie" element={<Nis2TriPilireBlog />} />
          <Route path="/nis2-reality-check-2025-vetsina-firem-neni-ready-a-cas-nebezi-pro-vas" element={<Nis2RealityCheckBlog />} />
          <Route path="/nis2-a-dodavatelske-retezce-proc-uz-nestaci-zabezpecit-jen-vlastni-it" element={<DodavatelskeRetezceBlog />} />
          <Route path="/samoidentifikace-podle-nis2-prakticky-navod-pro-ceske-firmy" element={<SamoidentifikaceNis2Blog />} />
          <Route path="/nis2-a-incidenty-co-musite-hlasit-kdy-a-jak-to-v-praxi-zvladnout" element={<IncidentyNis2Blog />} />
          <Route path="/aplikace" element={<AplikacePage />} />
          
          {/* Produkty a služby */}
          <Route path="/webinar" element={<WebinarPage />} />
          <Route path="/videokurz" element={<Navigate to="/webinar" replace />} />
          <Route path="/ebook-1" element={<Ebook1Page />} />
          <Route path="/ebook-2" element={<Ebook2Page />} />
          
          {/* Ostatní */}
          <Route path="/o-nas" element={<About />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/about" element={<Navigate to="/o-nas" replace />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/souhlas-se-zpracovanim-osobnich-udaju" element={<GdprPage />} />
          <Route path="/dekujeme" element={<Dekujeme />} />
          <Route path="/faq" element={<FaqPage />} />
          
          {/* Redirects - staré URL */}
          <Route path="/hp" element={<Navigate to="/" replace />} />
          <Route path="/about_desktop" element={<Navigate to="/kontakt" replace />} />
          <Route path="/pricing_desktop" element={<Navigate to="/pricing" replace />} />
          
          {/* 404 Fallback */}
          <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
}

export default App;